import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Carousel from "react-multi-carousel";
import BaseCard from "../BaseCard/BaseCard";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { useGetAgencyDashboardEmployeePerformanceQuery } from "../../redux/api/authApi";
import dayjs from "dayjs";

export const Performers = ({ currentAgency }) => {
  const [filterOpen, setFilterOpen] = useState(false);
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs());

  const formattedStart = startDate
    ? dayjs(startDate).format("YYYY-MM-DD")
    : dayjs().startOf("month").format("YYYY-MM-DD");
  const formattedEnd = endDate
    ? dayjs(endDate).format("YYYY-MM-DD")
    : dayjs().format("YYYY-MM-DD");

  const {
    data: employeePerformance = [],
    isLoading,
    refetch,
  } = useGetAgencyDashboardEmployeePerformanceQuery(
    {
      agcId: currentAgency?.agencyid,
      fromDate: formattedStart,
      toDate: formattedEnd,
    },
    {
      skip: !currentAgency?.agencyid, // ensures query waits for agency id
    }
  );

  // ✅ Refetch when dates change automatically (optional)
  useEffect(() => {
    if (currentAgency?.agencyid) refetch();
  }, [formattedStart, formattedEnd, currentAgency?.agencyid]);

  const applyFilter = async () => {
    setFilterOpen(false);
    await refetch();
  };

  const employeeOfTheMonth = (employeePerformance || []).slice(0, 10);

  const responsive = {
    superLargeDesktop: { breakpoint: { max: 4000, min: 3000 }, items: 5 },
    desktop: { breakpoint: { max: 3000, min: 1024 }, items: 4 },
    tablet: { breakpoint: { max: 1024, min: 464 }, items: 2 },
    mobile: { breakpoint: { max: 464, min: 0 }, items: 1 },
  };

  const CustomLeftArrow = ({ onClick }) => (
    <Button
      onClick={onClick}
      sx={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        position: "absolute",
        left: "-1.1rem",
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 2,
        color: "#56C662",
        fontWeight: "bolder",
      }}
    >
      <ArrowBackIos fontSize="medium" />
    </Button>
  );

  const CustomRightArrow = ({ onClick }) => (
    <Button
      onClick={onClick}
      sx={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        position: "absolute",
        right: "-0.1rem",
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 2,
        color: "#56C662",
        fontWeight: "bolder",
      }}
    >
      <ArrowForwardIos fontSize="medium" />
    </Button>
  );

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(285px,1fr))",
      }}
    >
      <BaseCard title={"Employees Performance"} sx={{ flex: 1 }}>
        <Box
          sx={{
            width: "100%",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 1,
          }}
        >
          {/* ✅ Filter Button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", pb: 1.5}}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setFilterOpen(true)}
            >
              Filter
            </Button>
          </Box>
          <Divider />

          {/* ✅ Employee Performance List */}
          <ListItem sx={{ fontWeight: "bold" }}>
            <ListItemText primary="Staff" sx={{ flex: 1 }} />
            <ListItemText primary="No. of Deals" sx={{ flex: 1 }} />
            <ListItemText primary="Amount" sx={{ flex: 1 }} />
            <ListItemText primary="No. of Leads" sx={{ flex: 1 }} />
            <ListItemText primary="Last Deal Date" sx={{ flex: 1 }} />
          </ListItem>
          <Divider />

          <List
            disablePadding
            dense
            sx={{
              width: "100%",
              bgcolor: "background.paper",
              overflow: "auto",
              maxHeight: 300,
            }}
          >
            {isLoading ? (
              <Typography textAlign="center" sx={{ p: 2 }}>
                Loading...
              </Typography>
            ) : employeePerformance.length > 0 ? (
              employeePerformance.map((emp, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemText
                      primary={emp?.firstName || "N/A"}
                      sx={{ flex: 1 }}
                    />
                    <ListItemText
                      primary={emp?.dealtotal || 0}
                      sx={{ flex: 1 }}
                    />
                    <ListItemText
                      primary={emp?.dealsum || 0}
                      sx={{ flex: 1 }}
                    />
                    <ListItemText
                      primary={emp?.leadscount || 0}
                      sx={{ flex: 1 }}
                    />
                    <ListItemText
                      primary={
                        emp?.lastDealDate
                          ? dayjs(emp.lastDealDate).format("DD MMM YYYY")
                          : "—"
                      }
                      sx={{ flex: 1 }}
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))
            ) : (
              <Typography
                textAlign="center"
                sx={{ p: 2, color: "text.secondary" }}
              >
                No employee performance data available
              </Typography>
            )}
          </List>
        </Box>

        {/* ✅ Top Performers Carousel */}
        <Divider />
        <Box mt={4}>
          <Typography textAlign="center" variant="h5" mb={2}>
            Top Performers of the Month
          </Typography>

          {employeeOfTheMonth.length > 0 ? (
            <Carousel
              itemClass="carousel-item-spacing"
              responsive={responsive}
              customLeftArrow={<CustomLeftArrow />}
              customRightArrow={<CustomRightArrow />}
            >
              {employeeOfTheMonth.map((emp, ind) => (
                <Box
                  key={ind}
                  sx={{
                    p: 2,
                    borderLeft: `6px solid ${
                      ["#4CAF50", "#FF9800", "#F44336", "#2196F3", "#153580"][
                        ind % 5
                      ]
                    }`,
                    borderRadius: 2,
                    boxShadow: 1,
                    mr: 2,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 25,
                        height: 25,
                        borderRadius: "50%",
                        bgcolor: [
                          "#4CAF50",
                          "#FF9800",
                          "#F44336",
                          "#2196F3",
                          "#153580",
                        ][ind % 5],
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    >
                      {ind + 1}
                    </Box>
                    <Typography variant="subtitle1">
                      {emp?.firstName}
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between" mt={2}>
                    <Box>
                      <Typography color="gray" variant="body2">
                        Deals
                      </Typography>
                      <Typography>{emp?.dealtotal || 0}</Typography>
                    </Box>
                    <Box>
                      <Typography color="gray" variant="body2">
                        Amount
                      </Typography>
                      <Typography>{emp?.dealsum || 0}</Typography>
                    </Box>
                    <Box>
                      <Typography color="gray" variant="body2">
                        Leads
                      </Typography>
                      <Typography>{emp?.leadscount || 0}</Typography>
                    </Box>
                  </Box>

                  {emp?.lastDealDate && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mt={1}
                      textAlign="right"
                    >
                      Last Deal: {dayjs(emp.lastDealDate).format("DD MMM YYYY")}
                    </Typography>
                  )}
                </Box>
              ))}
            </Carousel>
          ) : (
            <Typography textAlign="center" color="text.secondary">
              No top performers this month.
            </Typography>
          )}
        </Box>
      </BaseCard>

      {/* ✅ Filter Dialog */}
      <Dialog open={filterOpen} onClose={() => setFilterOpen(false)}>
        <DialogTitle>Filter by Date</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" gap={2} mt={1}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => {
                  if (newValue) setStartDate(dayjs(newValue));
                }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => {
                  if (newValue) setEndDate(dayjs(newValue));
                }}
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterOpen(false)}>Cancel</Button>
          <Button onClick={applyFilter} variant="contained">
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
